'use client';

import { Icon, Image } from '@petsmart-ui/sparky';
import Link from 'next/link';

import { getBaseUrlJoined, trackMainNavLinkClick, trackNavigationHeaderClick } from '@libs/analytics';
import { clsx, usePageLayout } from '@libs/common-next';
import { handleServiceClick } from '@libs/experience-api';
import type { MouseEvent } from 'react';
import type { DrawerMenuItemProps } from './types';
import { getCategoryPath, trackCategoryPath } from '../MainMenu/utils';
import { getOptiHeaderText } from '../../utils/getOptiHeaderText';

export function DrawerMenuItem({
    category,
    categoryStack,
    handleCategoryClick,
    closeMenu,
}: Readonly<DrawerMenuItemProps>) {
    const { isAuthenticated } = usePageLayout();

    const depth = categoryStack.length - 1;
    const classes = clsx(
        'category',
        `category--${category.type ?? 'text'}`,
        category.highlighted && 'highlighted',
        depth > 0 && ['category--thin']
    );
    const menuColor = clsx(getOptiHeaderText(category.displayText ?? '') === 'Deals' && 'menu-color');

    const categoryPath = categoryStack.slice(1).map(c => c.displayText || '');
    const newPath = getCategoryPath(categoryPath, category.displayText);
    const trackNewPath = trackCategoryPath(newPath);
    const webUrl = category.link ?? '#';

    function handleLinkClick(event: MouseEvent) {
        handleServiceClick({
            authType: isAuthenticated,
            event,
            link: category.link,
        });
        trackMainNavLinkClick(trackNewPath);
        trackNavigationHeaderClick(trackNewPath, getBaseUrlJoined(webUrl) ?? '', 'main nav');
        closeMenu();
    }
    function renderLink() {
        if (category.shopAllCategory && category.type === 'text') {
            return (
                <Link
                    className="category__link category__link--shop-all"
                    onClick={event => handleLinkClick(event)}
                    href={category.link ?? '#'}
                    prefetch={false} // https://petsmart.atlassian.net/browse/SB-325
                >
                    <span>{getOptiHeaderText(category.displayText ?? '')}</span>
                </Link>
            );
        }
        return (
            <Link
                className={category.shopAllCategory ? 'category__link category__link--shop-all' : 'category__link'}
                onClick={event => handleLinkClick(event)}
                href={category.link ?? '#'}
                prefetch={false} // https://petsmart.atlassian.net/browse/SB-325
            >
                {category.type === 'image' ? (
                    <>
                        <Image src={category.imageUrl ?? ''} alt={category.displayText ?? ''} loading="lazy" />
                        <span className="sparky-u-is-vishidden">{getOptiHeaderText(category.displayText ?? '')}</span>
                    </>
                ) : (
                    <span className={menuColor}>{getOptiHeaderText(category.displayText ?? '')}</span>
                )}
            </Link>
        );
    }
    return (
        <li className={classes} data-testid="header-drawer-menu-item" tabIndex={-1}>
            {category.categories?.length ? (
                <button onClick={() => handleCategoryClick(category)} className="category__button" type="button">
                    <span className={menuColor}>{getOptiHeaderText(category.displayText ?? '')}</span>
                    <Icon name="chevron-right" />
                </button>
            ) : (
                renderLink()
            )}
        </li>
    );
}
